import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import { invitesAPI } from '../api';
import { normalizeInviteDigits } from '../utils/inviteCode';

function UsernameDialog({
  open,
  onSubmit,
  onLoginWithCode,
  initialInviterUsername,
  initialInviteDigits,
}) {
  const [username, setUsername] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [inviterUsername, setInviterUsername] = useState(initialInviterUsername || '');
  const [inviteDigits, setInviteDigits] = useState(initialInviteDigits || '');
  const [teamName, setTeamName] = useState('');
  const [inviteType, setInviteType] = useState(null);
  const [previewInviterName, setPreviewInviterName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');

  useEffect(() => {
    if (open) {
      setInviterUsername(initialInviterUsername || '');
      setInviteDigits(initialInviteDigits || '');
    }
  }, [open, initialInviterUsername, initialInviteDigits]);

  useEffect(() => {
    if (!open) {
      setUsername('');
      setError('');
      setLoginCode('');
      setCodeError('');
      setTeamName('');
      setInviteType(null);
      setPreviewInviterName('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const inviter = inviterUsername.trim();
    const digits = normalizeInviteDigits(inviteDigits);
    if (!inviter || digits.length !== 3) {
      setTeamName('');
      setInviteType(null);
      setPreviewInviterName('');
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    (async () => {
      try {
        const response = await invitesAPI.preview(inviter, digits);
        if (cancelled) return;
        const data = response.data;
        setInviteType(data.type || null);
        setPreviewInviterName(data.inviterUsername || inviter);
        setTeamName(data.teamName || '');
        setError('');
      } catch (previewError) {
        if (cancelled) return;
        setInviteType(null);
        setTeamName('');
        setPreviewInviterName('');
        setError(previewError.response?.data?.error || 'Invalid invite code');
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, inviterUsername, inviteDigits]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    const inviter = inviterUsername.trim();
    const digits = normalizeInviteDigits(inviteDigits);
    if (!inviter) {
      setError('Inviter username is required');
      return;
    }
    if (digits.length !== 3) {
      setError('Invite code must be 3 digits');
      return;
    }
    if (!inviteType) {
      setError(error || 'Invalid invite code');
      return;
    }

    setError('');
    onSubmit(username.trim(), inviter, digits);
  };

  const handleLoginWithCode = (e) => {
    e.preventDefault();
    const code = loginCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setCodeError('Enter a 6-digit code');
      return;
    }
    setCodeError('');
    onLoginWithCode?.(code);
  };

  const hasValidInvite = !!inviteType && !previewLoading;
  const preferInvite = Boolean(initialInviterUsername || initialInviteDigits);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Log In</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2">Login with 6-digit code</Typography>
          <Box component="form" onSubmit={handleLoginWithCode} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              autoFocus={!preferInvite}
              fullWidth
              label="6-digit code"
              value={loginCode}
              onChange={(e) => {
                setLoginCode(e.target.value);
                setCodeError('');
              }}
              error={!!codeError}
              helperText={codeError || 'Enter the code you received from an invite'}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            />
            <Button type="submit" variant="contained" onClick={handleLoginWithCode}>
              Log in
            </Button>
          </Box>

          <Divider sx={{ my: 1 }} />

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Create your account (invite required)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Inviter username"
                  value={inviterUsername}
                  onChange={(e) => {
                    setInviterUsername(e.target.value);
                    setError('');
                  }}
                  autoFocus={preferInvite}
                />
                <TextField
                  label="Code"
                  value={inviteDigits}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 3);
                    setInviteDigits(next);
                    setError('');
                  }}
                  inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                  sx={{ width: 110, flexShrink: 0 }}
                />
              </Box>
              {previewLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Checking invite code...
                  </Typography>
                </Box>
              )}
              {inviteType === 'contact_invite' && previewInviterName && (
                <Typography variant="body2" color="info.main">
                  You were invited by <strong>{previewInviterName}</strong> as a direct contact.
                  You can chat without joining a team.
                </Typography>
              )}
              {inviteType === 'team_invite' && teamName && (
                <Typography variant="body2" color="info.main">
                  You will join team: <strong>{teamName}</strong>
                </Typography>
              )}
              <TextField
                fullWidth
                label="Your username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                error={!!error}
                helperText={error}
              />
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button type="submit" variant="outlined" disabled={!hasValidInvite}>
                  Continue
                </Button>
              </DialogActions>
            </Box>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default UsernameDialog;
